import React, { useEffect, useState } from 'react';
import {
  Box, Checkbox, Chip, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Typography, useMediaQuery, useTheme,
} from '@mui/material';
import PharmacyMedicineAutocomplete from './PharmacyMedicineAutocomplete';
import ControlledDrugBadge from './ControlledDrugBadge';
import { InvTextField } from './InvField';
import { IPD_COLORS } from '../theme';
import { formatCurrency, formatDate, daysUntilExpiry, lineTotal, computeLineDiscountAmount, parseMoney } from '../utils';
import { applyUserDiscountToBatchLine, displayDiscountPct } from '../utils/pharmacyDiscount';
import useInvRole from '../hooks/useInvRole';
import { INV_ACTIONS } from '../utils/inventoryRoles';
import {
  issueUomFactor, batchIssueUomFactor, issueUomCode, baseUomCode, maxIssueQty,
  issueQtyIssue, formatAvailableQty,
} from '../utils/uomConversionEntity';
import { batchRowKey, isLineForRow } from '../utils/batchRowIdentity';

const NEAR_EXPIRY_DAYS = 90;

/** A batch's selling price per ISSUE unit.
 *
 *  The catalogue restates it — the front end must never multiply a base price
 *  up itself. The one safe fallback is an item issued in its own base UOM
 *  (factor 1), where the base price already IS the issue-unit price. For any
 *  real conversion a missing restated price yields 0, which blocks the line
 *  rather than billing a strip at a tablet's price.
 */
function issueSalePrice(batch, item) {
  const factor = batchIssueUomFactor(batch, item);
  if (factor == null) return 0;
  const restated = parseMoney(batch?.sale_price_issue_uom) || parseMoney(batch?.mrp_issue_uom);
  if (restated) return restated;
  return factor === 1 ? (parseMoney(batch?.sale_price) || parseMoney(batch?.mrp) || 0) : 0;
}

/** A batch's MRP per ISSUE unit. Same rule as issueSalePrice. */
function issueMrp(batch, item) {
  const factor = batchIssueUomFactor(batch, item);
  if (factor == null) return 0;
  const restated = parseMoney(batch?.mrp_issue_uom);
  if (restated) return restated;
  return factor === 1 ? (parseMoney(batch?.mrp) || 0) : 0;
}

/** Why this batch line cannot be dispensed as entered, or null when it can. */
export function batchLineQtyIssue(bl) {
  if (!bl) return 'No batch selected';
  return issueQtyIssue({
    qty: bl.qty,
    // An absent factor means the line came from a flow that predates issue
    // units — it is already in base, so 1. An explicitly null one means
    // batchLineFromBatch could not resolve a conversion, which is a real error.
    factor: bl.uom_factor === undefined ? 1 : bl.uom_factor,
    availableBase: bl.available_qty,
    code: bl.uom_code,
  });
}

function batchAvailableQty(batch) {
  return Number(batch?.available_quantity ?? batch?.quantity ?? batch?.stock ?? 0) || 0;
}

function batchMaterialGroup(batch, item) {
  return batch?.material_group
    ?? item?._raw?.material_group?.group_name
    ?? item?._raw?.material_group?.name
    ?? item?._raw?.material_group
    ?? '';
}

function batchWac(item) {
  const wac = item?._raw?.wac ?? item?.wac;
  return wac != null && wac !== '' ? parseMoney(wac) : null;
}

function isExpired(expiryDate) {
  const days = daysUntilExpiry(expiryDate);
  return days != null && days < 0;
}

function isNearExpiry(expiryDate) {
  const days = daysUntilExpiry(expiryDate);
  return days != null && days >= 0 && days < NEAR_EXPIRY_DAYS;
}

/** Earliest-expiry, in-stock, non-expired batch — FEFO default. */
function pickFefoBatch(batches) {
  return (batches || [])
    .filter((b) => batchAvailableQty(b) > 0 && !isExpired(b.expiry_date))
    .sort((a, b) => new Date(a.expiry_date) - new Date(b.expiry_date))[0] || null;
}

function batchLineFromBatch(batch, item, qty = 1) {
  return {
    // UI-only. Never submitted: the payload still identifies a batch the way
    // the API expects, by batch_number and expiry.
    _row_key: batchRowKey(batch),
    batch_number: batch.batch_number,
    expiry_date: batch.expiry_date,
    // qty, rate and mrp are all in the ISSUE unit, so qty x rate is a real
    // line total. available_qty stays in the BASE unit, because that is what
    // the ledger counts — uom_factor is the only bridge between the two, and
    // it travels on the line so nothing downstream has to look the item up.
    qty,
    rate: issueSalePrice(batch, item),
    mrp: issueMrp(batch, item),
    // This batch's own pack size, not the item's. The line carries it so
    // nothing downstream has to look the batch up again.
    uom_factor: batchIssueUomFactor(batch, item),
    uom_code: issueUomCode(item),
    material_group: batchMaterialGroup(batch, item),
    wac: batchWac(item),
    available_qty: batchAvailableQty(batch),
    discount_percent: 0,
    inventory_transaction_id: batch.inventory_transaction_id || batch.transaction_id || null,
    inventory_transaction_item_id: batch.inventory_transaction_item_id || batch.transaction_item_id || batch.stock_balance_id || null,
  };
}

function BatchExpiry({ expired, nearExpiry, expiryDate }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
      {!expired && nearExpiry && (
        <Box sx={{
          width: 7, height: 7, borderRadius: '50%',
          backgroundColor: IPD_COLORS.warning.text, flexShrink: 0,
        }} />
      )}
      <Typography sx={{ fontSize: 12.5 }}>{formatDate(expiryDate)}</Typography>
      {expired && (
        <Chip label="Expired" size="small" sx={{
          height: 18, fontSize: 10, fontWeight: 700,
          backgroundColor: IPD_COLORS.error.bg, color: IPD_COLORS.error.text,
        }} />
      )}
    </Box>
  );
}

function SalePriceBlock({ salePrice, mrp, isSupervisor, wac }) {
  return (
    <>
      <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
        {formatCurrency(salePrice)}
      </Typography>
      {salePrice < mrp && (
        <Typography sx={{
          fontSize: 10.5, color: IPD_COLORS.textSecondary, textDecoration: 'line-through',
        }}>
          MRP {formatCurrency(mrp)}
        </Typography>
      )}
      {isSupervisor && (
        <Typography sx={{ fontSize: 10.5, color: IPD_COLORS.textSecondary }}>
          WAC {wac != null ? formatCurrency(wac) : '—'}
        </Typography>
      )}
    </>
  );
}

function BatchQtyField({
  selected, batchLine, qtyIssue, avail, batchFactor, issueUnit, disabled, onQtyChange,
}) {
  if (!selected) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.6, minWidth: 0 }}>
      <InvTextField
        type="number"
        value={batchLine?.qty ?? ''}
        error={qtyIssue ? true : undefined}
        helperText={qtyIssue || ''}
        inputProps={{ min: 1, max: maxIssueQty(avail, batchFactor), step: 1 }}
        disabled={disabled}
        onChange={(e) => {
          const raw = e.target.value;
          onQtyChange(raw === '' ? '' : Number(raw));
        }}
      />
      <Chip
        label={issueUnit || '—'}
        size="small"
        disabled
        sx={{ height: 24, fontSize: 10.5, fontWeight: 700, mt: 0.4, flexShrink: 0 }}
      />
    </Box>
  );
}

function BatchDiscountField({
  selected, rowKey, batchLine, discountDraft, setDiscountDraft, disabled, onDiscountChange,
}) {
  if (!selected) return null;
  return (
    <InvTextField
      type="number"
      value={
        discountDraft?.rowKey === rowKey
          ? discountDraft.value
          : String(displayDiscountPct(batchLine))
      }
      inputProps={{ min: 0, max: 100, step: '0.01' }}
      disabled={disabled}
      onFocus={() => setDiscountDraft({
        rowKey,
        value: String(displayDiscountPct(batchLine)),
      })}
      onChange={(e) => {
        const raw = e.target.value;
        setDiscountDraft({ rowKey, value: raw });
        if (raw === '' || raw === '.') return;
        const n = Number(raw);
        if (!Number.isFinite(n)) return;
        onDiscountChange(n);
      }}
      onBlur={() => setDiscountDraft(null)}
    />
  );
}

function CardMeta({ label, children, align = 'left' }) {
  return (
    <Box sx={{ minWidth: 0, textAlign: align }}>
      <Typography sx={{
        fontSize: 10, fontWeight: 700, color: IPD_COLORS.textSecondary,
        letterSpacing: '0.04em', textTransform: 'uppercase', mb: 0.25,
      }}>
        {label}
      </Typography>
      {children}
    </Box>
  );
}

/** Sum of Total Basic / Total Discount / Amount Payable across medicine lines. */
export function computeLinesTotal(lines) {
  return (lines || []).reduce((acc, line) => {
    (line.batchLines || []).forEach((bl) => {
      const discPct = Number(bl.discount_percent) || 0;
      const basic = Number(bl.qty || 0) * Number(bl.rate || 0);
      const discount = computeLineDiscountAmount(bl.qty, bl.rate, discPct);
      acc.totalBasic += basic;
      acc.totalDiscount += discount;
      acc.amountPayable += lineTotal(bl.qty, bl.rate, 0, discPct);
    });
    return acc;
  }, { totalBasic: 0, totalDiscount: 0, amountPayable: 0 });
}

/**
 * Batch-wise picker for one medicine grid row: search + FEFO-defaulted batch table
 * supporting splitting a requested quantity across multiple batches.
 */
export default function PharmacyBatchPicker({
  medicineLine,
  storeId,
  store = null,
  onMedicineChange,
  onBatchLinesChange,
  isSupervisor: isSupervisorProp,
  disabled,
  requireStore = true,
}) {
  const { can } = useInvRole();
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('md'));
  const isSupervisor = isSupervisorProp ?? can(INV_ACTIONS.CONTROLLED_OVERRIDE);
  const item = medicineLine.medicine;
  const batchLines = medicineLine.batchLines || [];
  const [discountDraft, setDiscountDraft] = useState(null);

  useEffect(() => {
    if (!item || batchLines.length) return;
    const fefo = pickFefoBatch(item.batches);
    if (fefo) onBatchLinesChange([batchLineFromBatch(fefo, item, 1)]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, item?.batches?.length]);

  function findBatchLine(batch) {
    const rowKey = batchRowKey(batch);
    return batchLines.find((bl) => isLineForRow(bl, rowKey, batch)) || null;
  }

  function toggleBatch(batch, checked) {
    if (checked) {
      onBatchLinesChange([...batchLines, batchLineFromBatch(batch, item, 1)]);
    } else {
      const rowKey = batchRowKey(batch);
      onBatchLinesChange(batchLines.filter((bl) => !isLineForRow(bl, rowKey, batch)));
    }
  }

  function updateBatchQty(batch, qty) {
    const rowKey = batchRowKey(batch);
    onBatchLinesChange(batchLines.map(
      (bl) => (isLineForRow(bl, rowKey, batch) ? { ...bl, qty } : bl),
    ));
  }

  function updateBatchDiscount(batch, pctRaw) {
    const rowKey = batchRowKey(batch);
    onBatchLinesChange(batchLines.map((bl) => (
      isLineForRow(bl, rowKey, batch) ? applyUserDiscountToBatchLine(bl, pctRaw) : bl
    )));
  }

  const batches = item?.batches || [];
  // Resolved once for the whole table: the unit quantities are typed in, the
  // unit stock is counted in, and the bridge between them.
  const factor = issueUomFactor(item);
  const issueUnit = issueUomCode(item);
  const baseUnit = baseUomCode(item);
  const mrpLabel = `MRP${issueUnit ? ' / ' + issueUnit : ''}`;
  const saleLabel = `Sale Price${issueUnit ? ' / ' + issueUnit : ''}${isSupervisor ? ' / WAC' : ''}`;
  const thSx = { whiteSpace: 'nowrap' };

  const batchRows = batches.map((batch, batchIndex) => {
    const rowKey = batchRowKey(batch);
    const expired = isExpired(batch.expiry_date);
    const nearExpiry = isNearExpiry(batch.expiry_date);
    const batchLine = findBatchLine(batch);
    const selected = Boolean(batchLine);
    const avail = batchAvailableQty(batch);
    const batchFactor = batchIssueUomFactor(batch, item);
    // A batch received in a different unit from the one being
    // dispensed keeps the item's standard conversion — say so
    // rather than let the arithmetic look wrong.
    const overriddenPack = factor != null && batchFactor !== factor;
    const mrp = issueMrp(batch, item);
    const salePrice = issueSalePrice(batch, item) || mrp;
    const wac = batchWac(item);
    // Blank while still typing is not an error yet — only a
    // committed value gets judged.
    const qtyIssue = batchLine && batchLine.qty !== '' && batchLine.qty != null
      ? batchLineQtyIssue(batchLine)
      : null;
    const checkboxDisabled = disabled || expired || avail <= 0 || batchFactor == null
      || maxIssueQty(avail, batchFactor) < 1;
    return {
      batch, batchIndex, rowKey, expired, nearExpiry, batchLine, selected, avail,
      batchFactor, overriddenPack, mrp, salePrice, wac, qtyIssue, checkboxDisabled,
    };
  });

  function editors(row) {
    return {
      qty: (
        <BatchQtyField
          selected={row.selected}
          batchLine={row.batchLine}
          qtyIssue={row.qtyIssue}
          avail={row.avail}
          batchFactor={row.batchFactor}
          issueUnit={issueUnit}
          disabled={disabled}
          onQtyChange={(qty) => updateBatchQty(row.batch, qty)}
        />
      ),
      discount: (
        <BatchDiscountField
          selected={row.selected}
          rowKey={row.rowKey}
          batchLine={row.batchLine}
          discountDraft={discountDraft}
          setDiscountDraft={setDiscountDraft}
          disabled={disabled}
          onDiscountChange={(pct) => updateBatchDiscount(row.batch, pct)}
        />
      ),
    };
  }

  return (
    <Box sx={{ minWidth: 0, width: '100%' }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap',
        mb: batches.length ? 1 : 0,
      }}>
        <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
          <PharmacyMedicineAutocomplete
            value={medicineLine.medicine_id}
            valueItem={item}
            storeId={storeId}
            store={store}
            disabled={disabled}
            requireStore={requireStore}
            onChange={(itemId, picked) => {
              onMedicineChange(itemId, picked);
              onBatchLinesChange([]);
            }}
          />
        </Box>
        <ControlledDrugBadge show={!!item?.controlled} />
        {(item?._raw?.material_group?.group_name || item?._raw?.material_group) && (
          <Chip
            label={item._raw.material_group?.group_name || item._raw.material_group}
            size="small"
            sx={{ height: 20, fontSize: 10 }}
          />
        )}
      </Box>

      {item?.controlled && (
        <Typography sx={{ fontSize: 11, color: IPD_COLORS.warning.text, mb: 1 }}>
          Prescription required
        </Typography>
      )}

      {/* No path from the dispense unit to the base unit means a quantity here
          cannot be turned into stock, and a price cannot be trusted. Say so and
          keep the batches unselectable rather than dispensing a guess. */}
      {item && factor == null && (
        <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.error.text, mb: 1, fontWeight: 600 }}>
          {issueUnit ? `No ${issueUnit} → ${baseUnit || 'base'} conversion for this item` : 'Dispense UOM unresolved'}
          {' — add the rule on the item master (UOM Conversion tab) before dispensing.'}
        </Typography>
      )}

      {!!batchRows.length && compact && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
          {batchRows.map((row) => {
            const fields = editors(row);
            return (
              <Box
                key={row.rowKey || `row-${row.batchIndex}`}
                sx={{
                  border: `1px solid ${row.selected ? IPD_COLORS.selectedBorder : IPD_COLORS.border}`,
                  borderRadius: '8px',
                  p: 1.25,
                  opacity: row.expired ? 0.5 : 1,
                  backgroundColor: row.selected ? IPD_COLORS.selectedBg : IPD_COLORS.cardBg,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                  <Checkbox
                    size="small"
                    checked={row.selected}
                    disabled={row.checkboxDisabled}
                    onChange={(e) => toggleBatch(row.batch, e.target.checked)}
                    sx={{ mt: -0.5, flexShrink: 0 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{
                      display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap',
                    }}>
                      <Typography sx={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700 }}>
                        {row.batch.batch_number || '—'}
                      </Typography>
                      <BatchExpiry
                        expired={row.expired}
                        nearExpiry={row.nearExpiry}
                        expiryDate={row.batch.expiry_date}
                      />
                    </Box>
                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 1,
                      mt: 1,
                    }}>
                      <CardMeta label="Available Qty">
                        <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
                          {row.avail
                            ? formatAvailableQty(
                              row.avail, row.batchFactor, issueUnit, baseUnit, { showLoose: true },
                            )
                            : '—'}
                        </Typography>
                        {row.overriddenPack && (
                          <Typography sx={{ fontSize: 10, fontWeight: 500, color: IPD_COLORS.textSecondary }}>
                            {`1 ${issueUnit || 'unit'} = ${row.batchFactor} ${baseUnit || 'base'} for this batch`}
                          </Typography>
                        )}
                      </CardMeta>
                      <CardMeta label={mrpLabel} align="right">
                        <Typography sx={{ fontSize: 12.5 }}>{formatCurrency(row.mrp)}</Typography>
                      </CardMeta>
                      <CardMeta label={saleLabel}>
                        <SalePriceBlock
                          salePrice={row.salePrice}
                          mrp={row.mrp}
                          isSupervisor={isSupervisor}
                          wac={row.wac}
                        />
                      </CardMeta>
                    </Box>
                    {row.selected && (
                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        gap: 1,
                        mt: 1.25,
                      }}>
                        <CardMeta label="Qty">{fields.qty}</CardMeta>
                        <CardMeta label="Discount %">{fields.discount}</CardMeta>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {!!batchRows.length && !compact && (
        <TableContainer sx={{
          border: `1px solid ${IPD_COLORS.border}`,
          borderRadius: '8px',
          mb: 1,
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
        }}>
          <Table size="small" sx={{ minWidth: 760 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: IPD_COLORS.panelHeaderBg }}>
                <TableCell sx={{ width: 40 }} />
                <TableCell sx={thSx}>Batch No.</TableCell>
                <TableCell sx={thSx}>Expiry</TableCell>
                <TableCell align="right" sx={thSx}>Available Qty</TableCell>
                <TableCell align="right" sx={thSx}>{mrpLabel}</TableCell>
                <TableCell align="right" sx={thSx}>{saleLabel}</TableCell>
                <TableCell sx={{ width: 140, ...thSx }}>Qty</TableCell>
                <TableCell sx={{ width: 110, ...thSx }}>Discount %</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {batchRows.map((row) => {
                const fields = editors(row);
                return (
                  <TableRow
                    key={row.rowKey || `row-${row.batchIndex}`}
                    sx={{ opacity: row.expired ? 0.5 : 1 }}
                  >
                    <TableCell>
                      <Checkbox
                        size="small"
                        checked={row.selected}
                        disabled={row.checkboxDisabled}
                        onChange={(e) => toggleBatch(row.batch, e.target.checked)}
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {row.batch.batch_number || '—'}
                    </TableCell>
                    <TableCell>
                      <BatchExpiry
                        expired={row.expired}
                        nearExpiry={row.nearExpiry}
                        expiryDate={row.batch.expiry_date}
                      />
                    </TableCell>
                    {/* Availability is read off base-unit stock but shown in the
                        unit the Qty box takes, so the two columns can be compared
                        by eye — and it is the same number "Max N" would quote. */}
                    <TableCell align="right" sx={{ fontSize: 12.5, fontWeight: 600 }}>
                      {row.avail
                        ? formatAvailableQty(
                          row.avail, row.batchFactor, issueUnit, baseUnit, { showLoose: true },
                        )
                        : '—'}
                      {row.overriddenPack && (
                        <Typography sx={{ fontSize: 10, fontWeight: 500, color: IPD_COLORS.textSecondary }}>
                          {`1 ${issueUnit || 'unit'} = ${row.batchFactor} ${baseUnit || 'base'} for this batch`}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 12.5 }}>{formatCurrency(row.mrp)}</TableCell>
                    <TableCell align="right">
                      <SalePriceBlock
                        salePrice={row.salePrice}
                        mrp={row.mrp}
                        isSupervisor={isSupervisor}
                        wac={row.wac}
                      />
                    </TableCell>
                    <TableCell>{fields.qty}</TableCell>
                    <TableCell>{fields.discount}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
